import React, { useEffect } from "react";
import { useLazyQuery } from "@apollo/client";
import { Link, useParams } from "react-router-dom";
import { lang } from "../../../middleware/lang";
import { LoaderDots } from "../../../middleware/loaders";
import { GET_STOCK_COUNT } from "./queries";
import {
  StockCountItem,
  COUNT_TYPE_LABELS,
  COUNT_STATUS_LABELS,
  VARIANCE_REASON_LABELS,
} from "./models";

type Params = { id: string };

const StockCountDetail: React.FC = () => {
  const { id } = useParams<Params>();

  const [fetchCount, { data, loading }] = useLazyQuery(GET_STOCK_COUNT, {
    fetchPolicy: "no-cache",
  });

  useEffect(() => {
    fetchCount({ variables: { id } });
    //eslint-disable-next-line
  }, [id]);

  const formatDate = (timestamp: string | null) => {
    if (!timestamp) return "—";
    try {
      const date = new Date(parseInt(timestamp));
      return date.toLocaleDateString("es-MX", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return timestamp;
    }
  };

  if (loading) return <LoaderDots fullSize />;
  if (!data?.getStockCount) return null;

  const count = data.getStockCount;
  const typeInfo = COUNT_TYPE_LABELS[count.count_type] || {
    label: count.count_type,
    labelEn: count.count_type,
  };
  const statusInfo = COUNT_STATUS_LABELS[count.status] || {
    label: count.status,
    labelEn: count.status,
    color: "secondary",
  };

  const items: StockCountItem[] = count.items || [];
  const itemsWithVariance = items.filter(
    (i) => i.variance != null && i.variance !== 0
  );
  const totalVarianceCost = items.reduce(
    (sum, i) => sum + (parseFloat(String(i.variance_cost)) || 0),
    0
  );

  return (
    <>
      <p>
        <Link to="/inventory/counts">
          <i className="fa fa-chevron-left" aria-hidden="true"></i>{" "}
          {lang("Conteos", "Counts")}
        </Link>
      </p>

      <div className="d-flex justify-content-between align-items-center mb-3">
        <h1 className="mb-0">
          {lang("Conteo", "Count")} #{count.id}{" "}
          <span className={`badge badge-${statusInfo.color} ml-2`}>
            {lang(statusInfo.label, statusInfo.labelEn)}
          </span>
        </h1>
      </div>

      {/* Summary cards */}
      <div className="row mb-3">
        <div className="col-lg-3">
          <div className="material_card p-3 text-center">
            <h5 className="text-muted mb-1">
              {lang("Total Items", "Total Items")}
            </h5>
            <h2 className="mb-0">{items.length}</h2>
          </div>
        </div>
        <div className="col-lg-3">
          <div className="material_card p-3 text-center">
            <h5 className="text-muted mb-1">
              {lang("Con Varianza", "With Variance")}
            </h5>
            <h2
              className={`mb-0 ${
                itemsWithVariance.length > 0 ? "text-warning" : "text-success"
              }`}
            >
              {itemsWithVariance.length}
            </h2>
          </div>
        </div>
        <div className="col-lg-3">
          <div className="material_card p-3 text-center">
            <h5 className="text-muted mb-1">
              {lang("Costo Varianza", "Variance Cost")}
            </h5>
            <h2
              className={`mb-0 ${
                totalVarianceCost < 0
                  ? "text-danger"
                  : totalVarianceCost > 0
                  ? "text-warning"
                  : "text-success"
              }`}
            >
              ${Number(totalVarianceCost).toFixed(2)}
            </h2>
          </div>
        </div>
        <div className="col-lg-3">
          <div className="material_card p-3 text-center">
            <h5 className="text-muted mb-1">{lang("Tipo", "Type")}</h5>
            <h2 className="mb-0">
              <small>{lang(typeInfo.label, typeInfo.labelEn)}</small>
            </h2>
          </div>
        </div>
      </div>

      {/* Info row */}
      <div className="row mb-3">
        <div className="col-lg-4">
          <div className="material_card p-3">
            <h5>{lang("Información", "Information")}</h5>
            <table className="table table-sm table-borderless mb-0">
              <tbody>
                <tr>
                  <td className="text-muted">
                    {lang("Sucursal", "Branch")}
                  </td>
                  <td className="font-weight-bold">
                    {count.branch?.name || "—"}
                  </td>
                </tr>
                <tr>
                  <td className="text-muted">
                    {lang("Inicio", "Started")}
                  </td>
                  <td>{formatDate(count.started_at)}</td>
                </tr>
                <tr>
                  <td className="text-muted">
                    {lang("Fin", "Completed")}
                  </td>
                  <td>{formatDate(count.completed_at)}</td>
                </tr>
                {count.notes && (
                  <tr>
                    <td className="text-muted">{lang("Notas", "Notes")}</td>
                    <td>{count.notes}</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Items table */}
      <div className="material_card p-3">
        <h5 className="mb-3">{lang("Detalle de Items", "Item Details")}</h5>
        <div className="table-responsive">
          <table className="table table-sm vertical_middle">
            <thead className="thead-light">
              <tr>
                <th>{lang("Ingrediente", "Ingredient")}</th>
                <th>{lang("Ubicación", "Location")}</th>
                <th>{lang("Unidad", "Unit")}</th>
                <th className="text-center">
                  {lang("Esperado", "Expected")}
                </th>
                <th className="text-center">
                  {lang("Contado", "Counted")}
                </th>
                <th className="text-center">
                  {lang("Varianza", "Variance")}
                </th>
                <th>{lang("Razón", "Reason")}</th>
                <th className="text-right">
                  {lang("Costo Var.", "Var. Cost")}
                </th>
              </tr>
            </thead>
            <tbody>
              {items.map((item: StockCountItem) => {
                const variance = parseFloat(String(item.variance)) || 0;
                const varianceCost =
                  parseFloat(String(item.variance_cost)) || 0;
                const hasVariance = variance !== 0;
                const reasonInfo = item.variance_reason
                  ? VARIANCE_REASON_LABELS[item.variance_reason]
                  : null;

                return (
                  <tr
                    key={item.id}
                    className={
                      hasVariance
                        ? variance < 0
                          ? "table-danger"
                          : "table-warning"
                        : ""
                    }
                  >
                    <td className="font-weight-bold">
                      {item.ingredient?.name || `#${item.ingredient_id}`}
                    </td>
                    <td>
                      <small className="text-muted">
                        {item.location || "—"}
                      </small>
                    </td>
                    <td>{item.unit || "—"}</td>
                    <td className="text-center">
                      {Number(item.expected_qty || 0).toFixed(2)}
                    </td>
                    <td className="text-center">
                      {item.counted_qty != null
                        ? Number(item.counted_qty).toFixed(2)
                        : "—"}
                    </td>
                    <td className="text-center">
                      {hasVariance ? (
                        <span
                          className={`font-weight-bold ${
                            variance < 0
                              ? "text-danger"
                              : "text-warning"
                          }`}
                        >
                          {variance > 0 ? "+" : ""}
                          {Number(variance).toFixed(2)}
                        </span>
                      ) : (
                        <span className="text-success">0</span>
                      )}
                    </td>
                    <td>
                      {reasonInfo ? (
                        <small>{lang(reasonInfo.label, reasonInfo.labelEn)}</small>
                      ) : item.variance_notes ? (
                        <small className="text-muted">{item.variance_notes}</small>
                      ) : (
                        "—"
                      )}
                    </td>
                    <td className="text-right">
                      {hasVariance ? (
                        <span
                          className={
                            varianceCost < 0
                              ? "text-danger font-weight-bold"
                              : "text-warning font-weight-bold"
                          }
                        >
                          ${Number(varianceCost).toFixed(2)}
                        </span>
                      ) : (
                        "—"
                      )}
                    </td>
                  </tr>
                );
              })}
              {items.length === 0 && (
                <tr>
                  <td colSpan={8} className="text-center text-muted py-4">
                    {lang("Sin items", "No items")}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
};

export default StockCountDetail;
